import { Form, Formik, FormikConfig } from "formik";
import { object, string } from "yup";
import React from "react";
import { Class } from "@common/types/class";
import { submitHelper } from "@helpers/form";
import { checkClassLabelConflict } from "@helpers/class";
import { useClasses } from "@api/class.api";
import { ButtonWithLoader } from "../../../general/ButtonWithLoader/ButtonWithLoader";
import { Button } from "../../../general/Button/Button";
import { StandardModalHeader } from "../../../general/Modal/Modal";
import { FormRow } from "../../../forms/FormRow";

export interface ClassSchema {
    name: string;
}

interface ClassFormProps {
    classInstance?: Class;
    onHide: () => void;
    isPending: boolean;
    onSubmitClass: (data: ClassSchema) => Promise<unknown>;
    parentClassId: string | null;
}

export const ClassForm: React.FC<ClassFormProps> = ({
    classInstance,
    onHide,
    isPending,
    onSubmitClass,
    parentClassId,
}) => {
    const { classes } = useClasses();

    const formConfig: FormikConfig<ClassSchema> = {
        initialValues: {
            name: classInstance?.label ?? "",
        },
        validate: (values) => {
            const errors: Partial<ClassSchema> = {};
            if (!values.name) {
                errors.name = "Required";
            }

            const { conflict, conflictingClass } = checkClassLabelConflict({
                classes,
                parentClassId:
                    classInstance?.parentClassId ?? parentClassId ?? null,
                label: values.name,
                targetClassId: classInstance?.id,
            });
            if (conflict) {
                if (
                    conflictingClass?.id ===
                    (classInstance?.parentClassId ?? parentClassId)
                ) {
                    errors.name = "Subclass can't have the parent's name";
                } else if (conflictingClass?.parentClassId) {
                    errors.name =
                        "Subclass with this name already exists in this class";
                } else {
                    errors.name = "Class with this name already exists";
                }
            }
            return errors;
        },
        validationSchema: object().shape({
            name: string().required(),
        }),
        onSubmit: submitHelper({
            loading: isPending,
            handler: async (data) => {
                await onSubmitClass({
                    name: data.name,
                });
                onHide();
            },
        }),
        validateOnMount: true,
    };

    return (
        <>
            <StandardModalHeader>
                {classInstance ? "Update" : "Create"} Class
            </StandardModalHeader>
            <Formik {...formConfig}>
                {(formik) => (
                    <Form>
                        <FormRow fieldName="name" label="Name" size="sm" />

                        <footer className="d-flex justify-content-end gap-2">
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={onHide}
                                size="lg"
                            >
                                Cancel
                            </Button>

                            <ButtonWithLoader
                                variant="default"
                                loading={isPending}
                                size="lg"
                                disabled={!formik.isValid}
                            >
                                {classInstance ? "Save" : "Create"}
                            </ButtonWithLoader>
                        </footer>
                    </Form>
                )}
            </Formik>
        </>
    );
};
